import styled from "styled-components";
import { useCustomPlayerModal, useSearchModal } from "../contexts/ModalContext";
import Modal from "./Modal";
import CloseBg from "../assets/images/bg_close.svg";
import IcReset from "../assets/images/ic_reset.svg";
import IcSearch from "../assets/images/ic_search.svg";
import { useState, useRef, useEffect } from "react";
import { useImageCache } from "../contexts/ImageCacheContext";
import SearchResultItem from "./SearchResultItem";
import fakeSearchPlayers from "../utils/fakeRepository";
import { useSquad } from "../contexts/SquadContext";
import { calculateSynergyStates } from "../utils/calculateSynergyStates";
import { usePlayerStore } from "../contexts/PlayerContext";
import { Virtuoso } from "react-virtuoso";
import { filterPlayers } from "../utils/filterPlayers";
import { SelectPopup } from "./SelectPopup";
import getAvailablePositions from "../utils/getAvailablePositions";
import CustomPlayerModal from "./CustomPlayerModal";

const ModalContainer = styled.div`
    width: 84rem;
    height: 54rem;
    background-color: white;
`;

const ModalHeader = styled.div`
    position: relative;
    height: 5.75rem;
    justify-content: center;
    align-items: center;
    display: flex;
    font-family: 'Esamanru', sans-serif;
    font-size: 1.5rem;
    font-weight: 200;
    color: #545454;
    border-bottom: 0.125rem solid #545454;
`

const CloseButton = styled.img`
    position: absolute;
    right: 1.625rem;
    width: 2.5rem;
    height: 2.5rem;
    cursor: pointer;
`

const HorizontalContainer = styled.div`
    height: 48.25rem;
    display: flex;
    flex-direction: row;
`

const SearchContainer = styled.div`
    position: relative;
    width: 38rem;
    display: flex;
    flex-direction: column;
    padding: 2rem 2rem;
    border-right: 0.125rem solid #545454;

`

const SearchBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 0.375rem;
    position: relative;
`

const SearchBarWrapper = styled.div`
    position: relative;
    flex: 1;
    height: 3.5rem;
`;

const SearchBar = styled.input`
    width: 100%;
    height: 100%;
    display: flex;
    border: 0.25rem solid #545454;
    font-family: 'Pretendard Variable';
    font-weight: 500;
    font-size: 1.25rem;
    padding: 0 3.5rem 0 1.875rem;
    color: #000000;
    box-sizing: border-box;
`

const ClearButton = styled.button`
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #999;
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;

    &:hover {
        color: #545454;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

const IconButton = styled.div`
    width: 4rem;
    height: 4rem;
    background-color: #545454;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
`

const Icon = styled.img`
    width: 2.5rem;
    height: 2.5rem;
`

const SearchFilterContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
`

const FilterItem = styled.div`
    display: flex;
    height: 2.75rem;
    justify-content: center;
    align-items: center;
    font-family: 'Pretendard Variable';
    font-weight: 600;
    font-size: 1.25rem;
    cursor: pointer;
`

const SelectedFilterItem = styled(FilterItem)`
    background-color: #545454;
    color: white;
`

const UnselectedFilterItem = styled(FilterItem)`
    background-color: white;
    color: #545454;
    border: 0.125rem solid #545454;
    box-sizing: border-box;
`

const TeamFilterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 0.5rem;
`

const PositionFilterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 0.5rem;
`

const TypeFilterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
`

const FilterVerticalContainer = styled.div`
`

const FilterTitle = styled.div`
    font-family: 'Pretendard Variable';
    font-size: 1.25rem;
    font-weight: 400;
    color: #545454;
    margin-bottom: 0.5rem;
    width: fit-content;
`

const SearchResultContainer = styled.div`
    width: 42rem;
    height: 48.25rem;
    // overflow: auto;
`

const PopupContainer = styled.div`
    width: 0rem;
    position: absolute;
    overflow: visible;
    z-index: 10;
    top: 8.425rem;
    left: 7.625rem;
`

const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`

const TopButton = styled.div`
    padding: 0.4375rem 1.225rem;
    width: fit-content;
    background-color: #545454;
    font-family: 'Pretendard Variable';
    font-size: 1rem;
    color: white;
    border-radius: 0.375rem;
    cursor: pointer;
`

export default function SearchModal() {

    const { isSearchModalOpen, openSearchModal, closeSearchModal } = useSearchModal();
    const { isCustomPlayerModalOpen, openCustomPlayerModal, closeCustomPlayerModal } = useCustomPlayerModal();
    const { playerInfos, addPlayer,  removePlayer } = useSquad();
    const { playersData, setPlayersData } = usePlayerStore();

    const [selectedPositions, setSelectedPositions] = useState([]);

    // 모달 열릴 때 선택 가능한 포지션 초기화
    useEffect(() => {
        if (isSearchModalOpen) {
            setSelectedPositions(getAvailablePositions(isSearchModalOpen));
        }
    }, [isSearchModalOpen]);

    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);

    const filterTeams = ['두산', 'LG', '키움', 'SSG', 'KT', 'KIA', '롯데', '삼성', 'NC', '한화'];
    const filterPositions = ['SP', 'RP', 'CP', 'LF', 'CF', 'RF', '1B', '2B', 'SS', '3B', 'C', 'DH'];
    const filterTypes = ['시즌', '라이브', '임팩트', '시그니쳐', '국가대표', '올스타', '골든글러브']

    const [isSortPopupVisible, setSortPopupVisible] = useState(false);
    const sortItems = ["OVR 순", "세트덱 스코어 순", "연도 순 - 오름차순", "연도 순 - 내림차순", "파워+정확/변화+구위 순"];

    const [sortKey, setSortKey] = useState("파워+정확/변화+구위 순");

    const [filteredPlayers, setFilteredPlayers] = useState([]);
    const [searchGeneration, setSearchGeneration] = useState(0);
    const virtuosoRef = useRef(null);
    const { getFromCache, addToCache } = useImageCache();

    const onResetClicked = () => {
        setSearchKeyword('');
        setSelectedTeams([]);
        setSelectedPositions([]);
        setSelectedTypes([]);
        setFilteredPlayers([]);
        setSearchGeneration(prev => prev + 1);
    }


    const onSelectedTeamClicked = (team) => {
        setSelectedTeams(selectedTeams.filter(t => t !== team));
    }
    const onUnSelectedTeamClicked = (team) => {
        setSelectedTeams([...selectedTeams, team]);
    }

    const onSelectedPositionClicked = (position) => {
        setSelectedPositions(selectedPositions.filter(p => p !== position));
    }
    const onUnSelectedPositionClicked = (position) => {
        setSelectedPositions([...selectedPositions, position]);
    }

    const onSelectedTypeClicked = (type) => {
        setSelectedTypes(selectedTypes.filter(t => t !== type));
    }
    const onUnSelectedTypeClicked = (type) => {
        setSelectedTypes([...selectedTypes, type]);
    }
    
    function checkPositionAvailable(position, player) {

        if (['SUB1', 'SUB2', 'SUB3', 'SUB4', 'SUB5', 'SUB6', 'DH'].includes(position)) {
            return ['LF', 'CF', 'RF', '1B', '2B', 'SS', '3B', 'C', 'DH'].includes(player.position);
        } else if (['SP1', 'SP2', 'SP3', 'SP4', 'SP5'].includes(position)) {
            return (player.position === 'SP');
        } else if (['RP1', 'RP2', 'RP3', 'RP4', 'RP5', 'RP6'].includes(position)) {
            return (player.position === 'RP');
        } else {
            return player.position === position;
        }
    }

    function checkDuplicatedPlayer(newPlayer, playerInfos) {
        const playerPositions = [
            'LF', 'CF', 'RF', '1B', '2B', '3B', 'SS', 'C', 'DH', 'SUB1', 'SUB2', 'SUB3', 'SUB4', 'SUB5', 'SUB6',
            'SP1', 'SP2', 'SP3', 'SP4', 'SP5', 'RP1', 'RP2', 'RP3', 'RP4', 'RP5', 'RP6', 'CP'
        ];

        const isDuplicated = playerPositions.some((key) => {
            return playerInfos[key] && playerInfos[key].playerName === newPlayer.playerName;
        });

        return !isDuplicated;
    }

    // 검색 실행
    const onSearchClicked = (keyword, teams, positions, types) => {
        const currentFilters = {
            keyword: keyword !== undefined ? keyword : searchKeyword,
            teams: teams !== undefined ? teams : selectedTeams,
            positions: positions !== undefined ? positions : selectedPositions,
            types: types !== undefined ? types : selectedTypes,
            sortKey: sortKey
        };

        const results = filterPlayers(playersData, currentFilters);
        setFilteredPlayers(results);
        setSearchGeneration(prev => prev + 1);

        // 스크롤을 맨 위로 초기화
        if (virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex(0);
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        onSearchClicked();
    };


    // player
    // id, originalOvr, year?, stars, playerName, description?, team, position, type, isPitcher
    // hittingAngle?, setDeckScore, originalStates, trainedStates, specialTrainedStates, postionTrainedStates, enforcedStates, awakenStates, setDeckStates, synergyStates,  level, isAwaken
    // states : level, s1, s2, s3, s4, s5, s6
    function makePlayerFull(player) {

        const synergyStates = calculateSynergyStates(player, {...playerInfos, [isSearchModalOpen]: player}, isSearchModalOpen);

        return {
            ...player,
            level: 0,
            isAwaken: false,
            isFa: null,
            faceOn : { id: null, url: null },
            trainedStates: { level: 0, s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            specialTrainedStates: { level: 0, s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            positionTrainedStates: { level: 0, s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            positionTrainedAdditionalStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            enforcedStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            awakenStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            setDeckStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
            synergyStates: synergyStates,
            faStates: {s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 },
        }
    }

    const onPlayerSelected = (player) => {

        const isPositionAvailable = checkPositionAvailable(isSearchModalOpen, player);
        const isDuplicatedPlayer = checkDuplicatedPlayer(player, playerInfos)

        if(isPositionAvailable && isDuplicatedPlayer) {
            addPlayer(isSearchModalOpen, makePlayerFull(player));
            closeSearchModal();
        } else {
            alert("해당 포지션에 배치할 수 없거나 중복된 선수입니다.");
        }
    }

    const dummyPlayers = fakeSearchPlayers("");

    return (
        <Modal
            isOpen={isSearchModalOpen} 
            onClose={closeSearchModal}
        >       
            <ModalContainer>
                <ModalHeader>
                    선수 검색 - {isSearchModalOpen}
                    <CloseButton 
                        src={CloseBg} 
                        onClick={closeSearchModal}
                    />
                </ModalHeader>

                <HorizontalContainer>
                    <SearchContainer>
                        <form onSubmit={handleFormSubmit}>
                            <SearchBarContainer>
                                <SearchBarWrapper>
                                    <SearchBar
                                        type="text"
                                        placeholder="선수명, 연도, 임팩트 종류 등을 입력해주세요"
                                        value={searchKeyword}
                                        onChange={(e) => setSearchKeyword(e.target.value)}
                                    />
                                    {searchKeyword && (
                                        <ClearButton
                                            type="button"
                                            onClick={() => setSearchKeyword('')}
                                            title="검색어 초기화"
                                        >
                                            ✕
                                        </ClearButton>
                                    )}
                                </SearchBarWrapper>

                                <IconButton as="button" type="submit">
                                    <Icon src={IcSearch} />
                                </IconButton>

                                <IconButton onClick={onResetClicked}>
                                    <Icon src={IcReset} />
                                </IconButton>
                            </SearchBarContainer>
                        </form>

                        <RowContainer>
                            <FilterTitle style={{marginTop: '0.5rem', cursor: 'pointer'}} onClick={() => {setSortPopupVisible(true)}}>정렬 기준 : {sortKey} ▼</FilterTitle>
                            
                        </RowContainer>
                        <SearchFilterContainer>

                            <PopupContainer>
                                <SelectPopup
                                    isVisible={isSortPopupVisible}
                                    gridCols={1}
                                    onClose={() => {setSortPopupVisible(false)}}
                                    onSelect={(sortKey) => {
                                        setSortKey(sortKey)
                                        setSortPopupVisible(false);
                                    }}
                                    initialValue={sortKey}
                                    items={sortItems}
                                />
                            </PopupContainer>

                            <FilterVerticalContainer>
                                <FilterTitle>구단</FilterTitle>
                                <TeamFilterGrid>
                                    {filterTeams.map((team) => (
                                        selectedTeams.includes(team) ? 
                                        <SelectedFilterItem onClick={() => {onSelectedTeamClicked(team)}} key={team}>{team}</SelectedFilterItem> :
                                        <UnselectedFilterItem onClick={() => {onUnSelectedTeamClicked(team)}} key={team}>{team}</UnselectedFilterItem>
                                    ))}
                                </TeamFilterGrid>
                            </FilterVerticalContainer>

                            <FilterVerticalContainer>
                                <FilterTitle>포지션</FilterTitle>
                                <PositionFilterGrid>
                                    {filterPositions.map((position) => (
                                        selectedPositions.includes(position) ? 
                                        <SelectedFilterItem onClick={() => {onSelectedPositionClicked(position)}} key={position}>{position}</SelectedFilterItem> :
                                        <UnselectedFilterItem onClick={() => {onUnSelectedPositionClicked(position)}} key={position}>{position}</UnselectedFilterItem>
                                    ))}
                                </PositionFilterGrid>
                            </FilterVerticalContainer>

                            <FilterVerticalContainer>
                                <FilterTitle>선수 종류</FilterTitle>
                                <TypeFilterGrid>
                                    {filterTypes.map((type) => (
                                        selectedTypes.includes(type) ? 
                                        <SelectedFilterItem onClick={() => {onSelectedTypeClicked(type)}} key={type}>{type}</SelectedFilterItem> :
                                        <UnselectedFilterItem onClick={() => {onUnSelectedTypeClicked(type)}} key={type}>{type}</UnselectedFilterItem>
                                    ))}
                                </TypeFilterGrid>
                            </FilterVerticalContainer>

                            <TopButton
                                onClick={() => {
                                    openCustomPlayerModal(isSearchModalOpen)
                                    closeSearchModal()
                                }}
                            >
                                커스텀 선수 생성하기
                            </TopButton>
                            
                        </SearchFilterContainer>
                    </SearchContainer>

                    <SearchResultContainer>
                        <Virtuoso
                            key={searchGeneration}
                            ref={virtuosoRef}
                            style={{ height: '100%', width: '100%' }}
                            totalCount={filteredPlayers.length}
                            itemContent={index => {
                                const player = filteredPlayers[index];
                                const playerId = `${player.playerName}-${player.team}`;
                                const cachedImageUrl = getFromCache(playerId);
                                return (
                                    <SearchResultItem
                                        player={player}
                                        onClick={() => { onPlayerSelected(player) }}
                                        cachedImageUrl={cachedImageUrl}
                                        onImageLoaded={(playerName, imageUrl) => {
                                            addToCache(playerId, imageUrl);
                                        }}
                                    />
                                );
                            }}
                        />
                    </SearchResultContainer>

                </HorizontalContainer>
            </ModalContainer>
        </Modal>
    )
}